import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTour } from '@/lib/tour/useTour';
import { useTourStore } from '@/lib/tour/useTourStore';
import { TOURS, tourIdForPath } from '@/lib/tour/tourContent';
import { useLegalGatePending } from '@/features/legal/LegalGate';

const MAX_ATTEMPTS = 12;
const RETRY_MS = 350;

/**
 * The only tour that opens by itself.
 *
 * Every page used to ambush its first visitor with a walkthrough: welcome on
 * the home, then another one the moment you saved your first xotski, then
 * another. Three interruptions before the first useful action, each of them
 * arriving while the person was mid-task.
 *
 * The others did not go away — every page keeps its walkthrough behind the
 * compass in the top bar, which is where someone who wants one goes looking.
 * A tour you asked for is help; a tour that opens on top of your work is an
 * interruption wearing help's clothes.
 */
const AUTOPLAY_TOUR_IDS = new Set(['home']);

/** How many of a tour's anchored steps are currently in the DOM. */
function countAnchors(tourId: string): { present: number; total: number } {
  const selectors = (TOURS[tourId]?.steps ?? [])
    .map((s) => s.element)
    .filter((s): s is string => Boolean(s));
  const present = selectors.filter((sel) => document.querySelector(sel)).length;
  return { present, total: selectors.length };
}

/**
 * Auto-starts the current page's tour the first time a user lands on it.
 * Mounted once inside AdminShell. Manual replays go through TourLauncher.
 *
 * Pages are lazy-loaded and their anchors mount over a few frames, so we poll
 * until the anchor set is COMPLETE (or it stops growing for two ticks, or we
 * run out of attempts) before starting — that way the walkthrough captures
 * every step instead of whatever happened to be mounted on the first frame.
 * Timers are cleared on route change so a half-loaded tour never fires on the
 * wrong page.
 */
export function TourController() {
  const { pathname, search } = useLocation();
  const { startTour } = useTour();
  const hasSeen = useTourStore((s) => s.hasSeen);
  const autoplayDisabled = useTourStore((s) => s.autoplayDisabled);
  // Never open over the mandatory legal acknowledgement. It used to, and the
  // first thing a new customer did was dismiss a tour to reach a consent form
  // they could not skip.
  const legalPending = useLegalGatePending();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (autoplayDisabled || legalPending) return;

    const tourId = tourIdForPath(pathname, search);
    if (!tourId || hasSeen(tourId)) return;
    if (!AUTOPLAY_TOUR_IDS.has(tourId)) return;

    let attempts = 0;
    let lastCount = -1;
    let stableTicks = 0;

    const tryStart = () => {
      // Bail if the user navigated away or saw the tour via another trigger.
      if (tourIdForPath(window.location.pathname) !== tourId) return;
      if (hasSeen(tourId)) return;

      const { present, total } = countAnchors(tourId);
      const settled =
        present === total || stableTicks >= 2 || attempts >= MAX_ATTEMPTS;

      if (present > 0 && settled) {
        startTour(tourId);
        return;
      }

      stableTicks = present === lastCount ? stableTicks + 1 : 0;
      lastCount = present;
      if (++attempts < MAX_ATTEMPTS) {
        timer.current = setTimeout(tryStart, RETRY_MS);
      }
    };

    timer.current = setTimeout(tryStart, 500);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // `search` entra porque la pestaña vive en la query: sin él, cambiar de
    // pestaña dentro de un evento no reevalúa qué recorrido toca.
  }, [pathname, search, hasSeen, autoplayDisabled, legalPending, startTour]);

  return null;
}
